// frontend/src/components/ChatIA.js
import React, { useState, useEffect, useRef } from 'react';
import ChatService from '../services/chatService';
import aiAssistantIcon from '../assets/icons/ai-assistant.png';
import '../style/components/ChatIA.css';

// Componente para el formulario de búsqueda contextual
const FormularioBusqueda = ({ campos, onEnviar, onCancelar }) => {
    const [valores, setValores] = useState({});

    const handleChange = (nombreCampo, valor) => {
        setValores(prev => ({ ...prev, [nombreCampo]: valor }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onEnviar(valores);
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="row g-3">
                {campos.map((campo, idx) => (
                    <div key={idx} className="col-md-6">
                        <label className="form-label small fw-semibold">{campo.label}</label>
                        {campo.tipo === 'select' ? (
                            <select
                                className="form-select form-select-sm"
                                value={valores[campo.nombre] || ''}
                                onChange={(e) => handleChange(campo.nombre, e.target.value)}
                            >
                                {campo.opciones.map((opcion, i) => (
                                    <option key={i} value={opcion.valor}>{opcion.texto}</option>
                                ))}
                            </select>
                        ) : campo.tipo === 'textarea' ? (
                            <textarea
                                className="form-control form-control-sm"
                                placeholder={campo.placeholder}
                                value={valores[campo.nombre] || ''}
                                onChange={(e) => handleChange(campo.nombre, e.target.value)}
                                rows="3"
                            />
                        ) : (
                            <input
                                type={campo.tipo}
                                className="form-control form-control-sm"
                                placeholder={campo.placeholder}
                                value={valores[campo.nombre] || ''}
                                onChange={(e) => handleChange(campo.nombre, e.target.value)}
                            />
                        )}
                    </div>
                ))}
            </div>
            <div className="d-flex gap-2 mt-3 justify-content-end">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancelar}>
                    Cancelar
                </button>
                <button type="submit" className="btn btn-sm btn-primary">
                    🔍 Buscar
                </button>
            </div>
        </form>
    );
};

// Componente para seleccionar opciones de estadísticas
const SelectorEstadisticas = ({ opciones, onSeleccionar, onCancelar }) => {
    return (
        <div className="selector-estadisticas">
            <p className="small text-muted mb-3">Seleccione el tipo de estadística que desea consultar:</p>
            <div className="row g-2">
                {opciones.map((opcion, idx) => (
                    <div key={idx} className="col-md-6">
                        <button
                            type="button"
                            className="btn btn-outline-primary w-100 text-start"
                            onClick={() => onSeleccionar(opcion.valor)}
                        >
                            <div className="d-flex align-items-center">
                                <span className="me-2">{opcion.label}</span>
                            </div>
                            <small className="text-muted d-block">{opcion.descripcion}</small>
                        </button>
                    </div>
                ))}
            </div>
            <div className="mt-3 text-end">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancelar}>
                    Cancelar
                </button>
            </div>
        </div>
    );
};

// Configuración de categorías disponibles por rol
const categoriasDisponiblesPorRol = {
    ADMIN: ['estadisticas', 'clientes', 'tramites', 'eventos', 'crear_evento', 'gastos', 'medicos'],
    PROGRAMADOR: ['estadisticas', 'clientes', 'tramites', 'eventos', 'crear_evento', 'gastos', 'medicos'],
    SUPERVISOR: ['estadisticas', 'clientes', 'tramites', 'eventos', 'crear_evento', 'gastos', 'medicos'],
    RTI: ['estadisticas', 'clientes', 'tramites', 'eventos', 'crear_evento'],
    ART: ['estadisticas', 'tramites', 'eventos', 'gastos', 'medicos'],
    RECEPCION: ['clientes', 'tramites', 'eventos']
};

const ChatIA = ({ usuarioId = 1, rol = 'ADMIN' }) => {
    const [mensajes, setMensajes] = useState([]);
    const [mensajeActual, setMensajeActual] = useState('');
    const [cargando, setCargando] = useState(false);
    const [conversacionId, setConversacionId] = useState(null);
    const [recordatorios, setRecordatorios] = useState([]);
    const [sugerencias, setSugerencias] = useState([]);
    const [mostrarSugerencias, setMostrarSugerencias] = useState(true);
    const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null);
    const [formularioVisible, setFormularioVisible] = useState(false);
    const [desplegableAbierto, setDesplegableAbierto] = useState(false);
    const mensajesEndRef = useRef(null);
    const inputRef = useRef(null);
    const formularioRef = useRef(null);
    const formRef = useRef(null);

    // Categorías con sus campos de búsqueda (todas disponibles)
    const todasLasCategorias = [
        { 
            id: "estadisticas",
            icono: "📊", 
            nombre: "Estadísticas",
            descripcion: "Ver estadísticas y reportes de trámites",
            opciones: [
                { valor: "general", label: "📈 Estadísticas Generales", descripcion: "Vista general de todos los trámites" },
                { valor: "por-estado", label: "📋 Por Estado", descripcion: "Distribución por estado actual" },
                { valor: "por-resultado", label: "✅ Por Resultado", descripcion: "Favorables vs Desfavorables" },
                { valor: "por-tiempo", label: "⏱️ Por Tiempo", descripcion: "Tiempos promedio por estado" }
            ]
        },
        { 
            id: "clientes",
            icono: "🔍", 
            nombre: "Búsqueda de Clientes",
            campos: [
                { nombre: "nombre", label: "Nombre", tipo: "text", placeholder: "Ej: Juan" },
                { nombre: "apellido", label: "Apellido", tipo: "text", placeholder: "Ej: Pérez" },
                { nombre: "cuil", label: "CUIL", tipo: "text", placeholder: "Ej: 20-12345678-9" }
            ],
            descripcion: "Buscar por nombre, apellido o CUIL"
        },
        { 
            id: "tramites",
            icono: "📋", 
            nombre: "Consulta de Trámites",
            campos: [
                { nombre: "idTramite", label: "ID Trámite", tipo: "number", placeholder: "Ej: 123" },
                { 
                    nombre: "estado", 
                    label: "Estado", 
                    tipo: "select",
                    opciones: [
                        { valor: "", texto: "-- Seleccione un estado --" },
                        { valor: "Ingreso del Trámite", texto: "Ingreso del Trámite" },
                        { valor: "Turno médico asignado", texto: "Turno médico asignado" },
                        { valor: "Espera de documentación", texto: "Espera de documentación" },
                        { valor: "Aprobación médica", texto: "Aprobación médica" },
                        { valor: "Turno Anses", texto: "Turno Anses" },
                        { valor: "Turno Junta Médica", texto: "Turno Junta Médica" },
                        { valor: "Espera de 1° Dictamen", texto: "Espera de 1° Dictamen" },
                        { valor: "Completo", texto: "Completo" },
                        { valor: "Primera Apelación", texto: "Primera Apelación" },
                        { valor: "Segunda Apelación", texto: "Segunda Apelación" },
                        { valor: "Instancia Judicial", texto: "Instancia Judicial" },
                        { valor: "Sentencia Judicial", texto: "Sentencia Judicial" },
                        { valor: "Rechazado", texto: "Rechazado" }
                    ]
                },
                { nombre: "clienteCuil", label: "CUIL del Cliente", tipo: "text", placeholder: "Ej: 20-12345678-9" }
            ],
            descripcion: "Consultar por ID, estado o cliente"
        },
        { 
            id: "eventos",
            icono: "📅", 
            nombre: "Consultar Eventos",
            campos: [
                { nombre: "idTramite", label: "ID Trámite", tipo: "number", placeholder: "Ej: 123" },
                { nombre: "fechaDesde", label: "Desde", tipo: "date" },
                { nombre: "fechaHasta", label: "Hasta", tipo: "date" }
            ],
            descripcion: "Consultar eventos por trámite o rango de fechas"
        },
        { 
            id: "crear_evento",
            icono: "➕📅", 
            nombre: "Crear Evento",
            campos: [
                { nombre: "titulo", label: "Título", tipo: "text", placeholder: "Ej: Reunión con cliente", requerido: true },
                { nombre: "fecha", label: "Fecha", tipo: "date", requerido: true },
                { nombre: "horaInicio", label: "Hora de Inicio", tipo: "time", placeholder: "Ej: 14:00" },
                { 
                    nombre: "tipoEvento", 
                    label: "Tipo de Evento", 
                    tipo: "select",
                    opciones: [
                        { valor: "Reunión", texto: "Reunión" },
                        { valor: "Presentación", texto: "Presentación" },
                        { valor: "Audiencia", texto: "Audiencia" },
                        { valor: "Vencimiento", texto: "Vencimiento" },
                        { valor: "Notificación", texto: "Notificación" },
                        { valor: "Recordatorio", texto: "Recordatorio" },
                        { valor: "Turno Médico", texto: "Turno Médico" },
                        { valor: "Turno Anses", texto: "Turno Anses" },
                        { valor: "Junta Médica", texto: "Junta Médica" },
                        { valor: "Otro", texto: "Otro" }
                    ],
                    requerido: true
                },
                { nombre: "descripcion", label: "Descripción", tipo: "textarea", placeholder: "Detalles del evento (opcional)" }
            ],
            descripcion: "Crear un nuevo evento en el calendario"
        },
        { 
            id: "gastos",
            icono: "💰", 
            nombre: "Gastos Médicos",
            campos: [
                { nombre: "clienteCuil", label: "CUIL del Cliente", tipo: "text", placeholder: "Ej: 20123456789" },
                { nombre: "nombreMedico", label: "Nombre del Médico", tipo: "text", placeholder: "Ej: Juan Pérez" }
            ],
            descripcion: "Consultar gastos por cliente o médico"
        },
        { 
            id: "medicos",
            icono: "👨‍⚕️", 
            nombre: "Médicos",
            campos: [
                { nombre: "nombre", label: "Nombre", tipo: "text", placeholder: "Ej: Juan" },
                { nombre: "apellido", label: "Apellido", tipo: "text", placeholder: "Ej: Pérez" },
                { nombre: "matricula", label: "Matrícula", tipo: "text", placeholder: "Ej: 12345" }
            ],
            descripcion: "Buscar por nombre, apellido o matrícula"
        }
    ];

    // Filtrar categorías según el rol del usuario
    const categorias = todasLasCategorias.filter(cat => 
        categoriasDisponiblesPorRol[rol]?.includes(cat.id) ?? false
    );

    // Mensaje de bienvenida personalizado según rol
    const obtenerMensajeBienvenida = () => {
        switch (rol) {
            case 'ART':
                return 'Buenos días. Soy Themis, su asistente especializado del Área ART del Estudio Jurídico. Estoy a su disposición para consultas sobre trámites ART, clientes y eventos. ¿En qué puedo asistirle?';
            case 'RTI':
                return 'Buenos días. Soy Themis, su asistente especializado del Área RTI del Estudio Jurídico. Estoy a su disposición para consultas sobre trámites RTI, clientes y eventos. ¿En qué puedo asistirle?';
            case 'RECEPCION':
                return 'Buenos días. Soy Themis, su asistente del Estudio Jurídico. Estoy a su disposición para consultas sobre clientes, trámites y eventos. ¿En qué puedo asistirle?';
            case 'ADMIN':
            default:
                return 'Buenos días. Soy Themis, su asistente especializado del Estudio Jurídico. Estoy a su disposición para consultas sobre clientes, expedientes, trámites y eventos del estudio. ¿En qué puedo asistirle?';
        }
    };

    useEffect(() => {
        // Cargar recordatorios y sugerencias de forma asincrónica sin bloquear UI
        const inicializar = async () => {
            try {
                // Agregar mensaje de bienvenida PRIMERO
                setMensajes([{
                    contenido: obtenerMensajeBienvenida(),
                    esUsuario: false,
                    fechaCreacion: new Date(),
                    tipo: 'bienvenida'
                }]);
                
                // Luego cargar datos en background
                setTimeout(async () => {
                    try {
                        await cargarRecordatorios();
                        await cargarSugerencias();
                    } catch (error) {
                        console.error('Error al cargar datos iniciales:', error);
                    }
                }, 100);
            } catch (error) {
                console.error('Error al inicializar chat:', error);
            }
        };
        
        inicializar();
        
        // Verificar recordatorios cada 5 minutos
        const interval = setInterval(() => {
            cargarRecordatorios().catch(e => console.error('Error en intervalo:', e));
        }, 300000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Auto-scroll al final cuando hay nuevos mensajes
        mensajesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [mensajes]);
    
    useEffect(() => {
        // Recargar sugerencias después de cada conversación completa (cada 4 mensajes)
        // Pero evitar que sea muy frecuente
        if (mensajes.length > 1 && mensajes.length % 4 === 0) {
            const timeout = setTimeout(() => {
                cargarSugerencias();
            }, 500);
            return () => clearTimeout(timeout);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mensajes]);

    const cargarRecordatorios = async () => {
        try {
            const response = await ChatService.obtenerRecordatorios(usuarioId);
            setRecordatorios(response.data);
            
            // Mostrar notificaciones de recordatorios nuevos
            if (response.data.length > 0) {
                const noNotificados = response.data.filter(r => !r.notificado);
                if (noNotificados.length > 0) {
                    mostrarRecordatorios(noNotificados);
                }
            }
        } catch (error) {
            console.error('Error al cargar recordatorios:', error);
        }
    };
    
    const cargarSugerencias = async () => {
        try {
            const response = await ChatService.obtenerSugerencias(usuarioId);
            setSugerencias(response.data);
        } catch (error) {
            console.error('Error al cargar sugerencias:', error);
        }
    };

    const mostrarRecordatorios = (recordatorios) => {
        const mensajeRecordatorios = recordatorios.map(r => {
            const fecha = new Date(r.fechaRecordatorio).toLocaleString('es-AR');
            return `📅 ${r.titulo} - ${fecha}`;
        }).join('\n');

        // Agregar mensaje del sistema con recordatorios
        const mensajeRecordatorio = {
            contenido: `🔔 Recordatorios:\n\n${mensajeRecordatorios}`,
            esUsuario: false,
            fechaCreacion: new Date(),
            tipo: 'recordatorio'
        };
        setMensajes(prev => [...prev, mensajeRecordatorio]);
    };

    const enviarConsultaRapida = (consulta) => {
        if (!consulta || cargando) return;
        setMensajeActual(consulta);
        setFormularioVisible(false);
        setCategoriaSeleccionada(null);
        setTimeout(() => {
            formRef.current?.requestSubmit();
        }, 0);
    };

    const enviarMensaje = async (e) => {
        e.preventDefault();
        if (!mensajeActual.trim() || cargando) return;

        setMostrarSugerencias(false);
        setFormularioVisible(false);
        setCategoriaSeleccionada(null);
        
        const mensajeUsuario = {
            contenido: mensajeActual,
            esUsuario: true,
            fechaCreacion: new Date()
        };

        setMensajes(prev => [...prev, mensajeUsuario]);
        setMensajeActual('');
        setCargando(true);

        try {
            const response = await ChatService.enviarMensaje(
                mensajeActual,
                usuarioId,
                conversacionId,
                rol // Pasar el rol al backend para filtrado contextual
            );

            // Actualizar conversacionId si es nueva
            if (!conversacionId && response.data.conversacionId) {
                setConversacionId(response.data.conversacionId);
            }

            // Actualizar historial completo
            if (response.data.historial) {
                const historialFormateado = response.data.historial.map(m => ({
                    contenido: m.contenido,
                    esUsuario: m.esUsuario,
                    fechaCreacion: new Date(m.fechaCreacion)
                }));
                setMensajes(historialFormateado);
            } else {
                // Solo agregar la respuesta
                const mensajeIA = {
                    contenido: response.data.respuesta,
                    esUsuario: false,
                    fechaCreacion: new Date()
                };
                setMensajes(prev => [...prev, mensajeIA]);
            }

            // Verificar si el usuario pidió un recordatorio
            if (mensajeActual.toLowerCase().includes('recordar') || 
                mensajeActual.toLowerCase().includes('recordatorio')) {
                // Extraer información del recordatorio del mensaje
                crearRecordatorioDesdeMensaje(mensajeActual, response.data.respuesta);
            }

        } catch (error) {
            console.error('Error al enviar mensaje:', error);
            const mensajeError = {
                contenido: 'Lo siento, hubo un error al procesar tu mensaje. Por favor, intenta nuevamente.',
                esUsuario: false,
                fechaCreacion: new Date(),
                tipo: 'error'
            };
            setMensajes(prev => [...prev, mensajeError]);
        } finally {
            setCargando(false);
            inputRef.current?.focus();
        }
    };

    const crearRecordatorioDesdeMensaje = async (mensajeUsuario, respuestaIA) => {
        try {
            // Intentar extraer información del recordatorio del mensaje
            const fechaRegex = /(\d{1,2})\/(\d{1,2})\/(\d{4})/;
            const match = mensajeUsuario.match(fechaRegex);
            
            let fechaRecordatorio = new Date();
            if (match) {
                fechaRecordatorio = new Date(`${match[3]}-${match[2]}-${match[1]}`);
            } else {
                // Si no hay fecha, usar mañana
                fechaRecordatorio.setDate(fechaRecordatorio.getDate() + 1);
            }

            const recordatorioData = {
                usuarioId,
                titulo: `Recordatorio: ${mensajeUsuario.substring(0, 50)}`,
                descripcion: mensajeUsuario,
                fechaRecordatorio: fechaRecordatorio.toISOString()
            };

            await ChatService.crearRecordatorio(recordatorioData);
            cargarRecordatorios();
        } catch (error) {
            console.error('Error al crear recordatorio:', error);
        }
    };

    const limpiarChat = () => {
        setMensajes([]);
        setConversacionId(null);
        setFormularioVisible(false);
        setCategoriaSeleccionada(null);
    };

    const seleccionarCategoria = (categoria) => {
        setDesplegableAbierto(false); // Cerrar dropdown
        
        if (categoria.id === "estadisticas") {
            // Para estadísticas, mostrar selector de opciones
            setCategoriaSeleccionada(categoria);
            setFormularioVisible(true);
        } else {
            // Para otras categorías, mostrar formulario
            setCategoriaSeleccionada(categoria);
            setFormularioVisible(true);
            
            // Hacer scroll al formulario después de que se renderice
            setTimeout(() => {
                formularioRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 100);
        }
    };

    const seleccionarOpcionEstadistica = (tipoEstadistica) => {
        let consulta = "";
        
        switch (tipoEstadistica) {
            case "general":
                consulta = "Mostrar estadísticas generales de trámites";
                break;
            case "por-estado":
                consulta = "Mostrar estadísticas de trámites por estado";
                break;
            case "por-resultado":
                consulta = "Mostrar estadísticas de trámites por resultado (favorables y desfavorables)";
                break;
            case "por-tiempo":
                consulta = "Mostrar estadísticas de tiempo promedio de trámites por estado";
                break;
            default:
                consulta = "Mostrar estadísticas generales de trámites";
        }
        
        enviarConsultaRapida(consulta);
    };

    const enviarConsultaDesdeFormulario = (datos) => {
        let consulta = "";
        
        switch (categoriaSeleccionada.id) {
            case "clientes":
                if (datos.cuil) {
                    consulta = `Buscar cliente con CUIL ${datos.cuil}`;
                } else if (datos.nombre && datos.apellido) {
                    consulta = `Buscar cliente con nombre ${datos.nombre} ${datos.apellido}`;
                } else if (datos.nombre) {
                    consulta = `Buscar clientes con nombre ${datos.nombre}`;
                } else if (datos.apellido) {
                    consulta = `Buscar clientes con apellido ${datos.apellido}`;
                } else {
                    consulta = "Listar todos los clientes activos";
                }
                break;
                
            case "tramites":
                if (datos.idTramite) {
                    consulta = `Mostrar información del trámite ${datos.idTramite}`;
                } else if (datos.estado) {
                    consulta = `Listar trámites en estado ${datos.estado}`;
                } else if (datos.clienteCuil) {
                    consulta = `Mostrar trámites del cliente con CUIL ${datos.clienteCuil}`;
                } else {
                    consulta = "Listar todos los trámites activos";
                }
                break;
                
            case "eventos":
                if (datos.idTramite) {
                    consulta = `Mostrar eventos del trámite ${datos.idTramite}`;
                } else if (datos.fechaDesde && datos.fechaHasta) {
                    consulta = `Mostrar eventos desde ${datos.fechaDesde} hasta ${datos.fechaHasta}`;
                } else if (datos.fechaDesde) {
                    consulta = `Mostrar eventos desde ${datos.fechaDesde}`;
                } else {
                    consulta = "Mostrar próximos eventos del calendario";
                }
                break;
                
            case "crear_evento":
                if (datos.titulo && datos.fecha) {
                    // Construir consulta para crear evento
                    consulta = `Crear evento llamado ${datos.titulo} el ${datos.fecha}`;
                    
                    if (datos.horaInicio) {
                        consulta += ` a las ${datos.horaInicio}`;
                    }
                    
                    if (datos.tipoEvento) {
                        consulta += ` de tipo ${datos.tipoEvento}`;
                    }
                    
                    if (datos.descripcion) {
                        consulta += ` con descripción: ${datos.descripcion}`;
                    }
                } else {
                    // Campos obligatorios faltantes
                    alert("Por favor complete los campos obligatorios: Título y Fecha");
                    return;
                }
                break;
                
            case "gastos":
                if (datos.nombreMedico) {
                    consulta = `Mostrar gastos médicos del médico ${datos.nombreMedico}`;
                } else if (datos.clienteCuil) {
                    consulta = `Mostrar gastos médicos del cliente con CUIL ${datos.clienteCuil}`;
                } else {
                    consulta = "Mostrar resumen total de gastos médicos";
                }
                break;
                
            case "medicos":
                if (datos.matricula) {
                    consulta = `Buscar médico con matrícula ${datos.matricula}`;
                } else if (datos.nombre && datos.apellido) {
                    consulta = `Buscar médico con nombre ${datos.nombre} ${datos.apellido}`;
                } else if (datos.apellido) {
                    consulta = `Buscar médico con nombre ${datos.apellido}`;
                } else if (datos.nombre) {
                    consulta = `Buscar médico con nombre ${datos.nombre}`;
                } else {
                    consulta = "Listar todos los médicos disponibles";
                }
                break;
                
            default:
                consulta = "Consulta general";
        }
        
        enviarConsultaRapida(consulta);
    };

    const cancelarFormulario = () => {
        setFormularioVisible(false);
        setCategoriaSeleccionada(null);
    };

    return (
        <div className="chat-container">
            <div className="chat-header">
                <h2>⚖️ Themis IA</h2>
                <div className="chat-actions">
                    <button onClick={cargarRecordatorios} className="btn-refresh" title="Actualizar recordatorios">
                        🔄
                    </button>
                    <button onClick={limpiarChat} className="btn-clear" title="Limpiar chat">
                        🗑️
                    </button>
                </div>
            </div>

            {recordatorios.length > 0 && (
                <div className="recordatorios-banner">
                    <span>🔔 Tienes {recordatorios.length} recordatorio(s) pendiente(s)</span>
                </div>
            )}

            <div className="chat-mensajes">
                {/* Formulario de búsqueda contextual */}
                {formularioVisible && categoriaSeleccionada && (
                    <div ref={formularioRef} className="formulario-busqueda card shadow-sm p-4 mb-3">
                        <h5 className="mb-3">
                            {categoriaSeleccionada.icono} {categoriaSeleccionada.nombre}
                        </h5>
                        <p className="text-muted small mb-3">{categoriaSeleccionada.descripcion}</p>
                        
                        {/* Si es estadísticas, mostrar selector de opciones */}
                        {categoriaSeleccionada.id === "estadisticas" && categoriaSeleccionada.opciones && (
                            <SelectorEstadisticas
                                opciones={categoriaSeleccionada.opciones}
                                onSeleccionar={seleccionarOpcionEstadistica}
                                onCancelar={cancelarFormulario}
                            />
                        )}
                        
                        {/* Para otras categorías, mostrar formulario de campos */}
                        {categoriaSeleccionada.id !== "estadisticas" && categoriaSeleccionada.campos && (
                            <FormularioBusqueda
                                campos={categoriaSeleccionada.campos}
                                onEnviar={enviarConsultaDesdeFormulario}
                                onCancelar={cancelarFormulario}
                            />
                        )}
                    </div>
                )}
                
                {mostrarSugerencias && sugerencias.length > 0 && mensajes.length === 0 && !formularioVisible && (
                    <div className="sugerencias-rapidas">
                        <p><strong>💡 Sugerencias rápidas basadas en tu historial:</strong></p>
                        <div className="sugerencias-botones">
                            {sugerencias.map((sug, i) => (
                                <button 
                                    key={i} 
                                    className="btn-sugerencia"
                                    onClick={() => {
                                        setMensajeActual(sug);
                                        inputRef.current?.focus();
                                    }}
                                >
                                    {sug}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                
                {mensajes.map((mensaje, index) => (
                    <div
                        key={index}
                        className={`message-container ${mensaje.esUsuario ? 'user-message' : 'assistant-message'}`}
                    >
                        {!mensaje.esUsuario && (
                            <img 
                                src={aiAssistantIcon} 
                                className="message-avatar" 
                                alt="Themis IA" 
                            />
                        )}
                        <div className={`message-bubble ${mensaje.esUsuario ? 'user-bubble' : 'assistant-bubble'}`}>
                            <div className="mensaje-contenido">
                                {mensaje.contenido.split('\n').map((linea, i) => (
                                    <p key={i}>{linea}</p>
                                ))}
                            </div>
                            <div className="mensaje-fecha">
                                {mensaje.fechaCreacion.toLocaleTimeString('es-AR', { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                })}
                            </div>
                        </div>
                        {mensaje.esUsuario && (
                            <div className="user-avatar">👤</div>
                        )}
                    </div>
                ))}
                
                {cargando && (
                    <div className="mensaje mensaje-ia cargando">
                        <div className="mensaje-contenido">
                            <span className="typing-indicator">●</span>
                            <span className="typing-indicator">●</span>
                            <span className="typing-indicator">●</span>
                        </div>
                    </div>
                )}
                
                <div ref={mensajesEndRef} />
            </div>

            {/* Menú desplegable de accesos rápidos (arriba del input) */}
            {!formularioVisible && desplegableAbierto && (
                <div className="accesos-rapidos-desplegable p-3 rounded mb-2" style={{
                    position: 'absolute',
                    bottom: '70px',
                    left: '20px',
                    right: '20px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    backgroundColor: '#ffffff',
                    border: '2px solid #0d6efd',
                    boxShadow: 'var(--shadow-md)'
                }}>
                    <div className="d-flex flex-column gap-2">
                        {categorias.map((cat) => (
                            <button
                                key={cat.id}
                                className="btn btn-outline-primary d-flex align-items-center gap-2 text-start"
                                onClick={() => {
                                    seleccionarCategoria(cat);
                                    setDesplegableAbierto(false);
                                }}
                                style={{
                                    borderRadius: '8px',
                                    fontSize: '0.9rem',
                                    padding: '0.75rem'
                                }}
                                title={cat.descripcion}
                            >
                                <span style={{ fontSize: '1.5rem' }}>{cat.icono}</span>
                                <div className="d-flex flex-column align-items-start">
                                    <span className="fw-semibold">{cat.nombre}</span>
                                    <small className="text-muted" style={{ fontSize: '0.8rem' }}>{cat.descripcion}</small>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <form ref={formRef} onSubmit={enviarMensaje} className="chat-input-form">
                <button
                    type="button"
                    onClick={() => setDesplegableAbierto(!desplegableAbierto)}
                    className="btn-acceso-rapido"
                    title="Accesos Rápidos"
                    disabled={formularioVisible}
                    style={{
                        padding: '10px 15px',
                        backgroundColor: desplegableAbierto ? '#0d6efd' : '#e3f2fd',
                        color: desplegableAbierto ? '#fff' : '#0d6efd',
                        border: '2px solid #0d6efd',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        minWidth: '45px',
                        fontWeight: '600',
                        boxShadow: desplegableAbierto ? '0 2px 8px rgba(13, 110, 253, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}
                >
                    💡
                </button>
                <input
                    ref={inputRef}
                    type="text"
                    value={mensajeActual}
                    onChange={(e) => setMensajeActual(e.target.value)}
                    placeholder="Escribe tu mensaje aquí..."
                    disabled={cargando}
                    className="chat-input"
                />
                <button
                    type="submit"
                    disabled={!mensajeActual.trim() || cargando}
                    className="btn-enviar"
                >
                    {cargando ? '⏳' : '📤'}
                </button>
            </form>
        </div>
    );
};

export default ChatIA;

